import React from 'react';
import styled from 'styled-components';
import {TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import tabNames from '../../constants/tabNames';
import ltiTabNames from '../../constants/ltiTabNames';
import {adminAccessibleTabs, viewerAccessibleTabs, captionAccessibileTabs, adminAccessibleImageTabs} from '../../constants/tabPrivileges';
import {UpsertQueryParamInUrl} from '../util/QueryParamsHelper';
import AccessibilityTab from './accessibility/AccessibilityTab';
import AnalyticsTab from './analytics/AnalyticsTab';
import EditMediaTab from './edit-media/EditMediaTab';
import PrivacyTab from './privacy/PrivacyTab';
import QuizzingTab from './quizzing/QuizzingTab';
import ConversationsTab from './conversations/ConversationsTab';
import Tabs from './Tabs';
import {ITab} from '../../model/tab';
import quizApi from '../../service/quizApi';
import config from '../../service/config';
import DetailsTab from './details/DetailsTab';
import {SetupWorkerState} from '../../model/workerState';
import captionApi from '../../service/captionApi';
import {updateAnalyticsDataFromServer} from '../../context/analyticsDataProvider';
import {updateConversationsFromServer} from '../../context/conversationsProvider';
import Constants from '../../constants/Constants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import {updateQuizData} from '../../context/quizDataProvider';
import {mediaStore} from '../../context/mediaProvider';
import {updateCaptionDataState} from '../../context/captionDataProvider';
import ConversationTab from './conversations/ConversationTab';

const BaseTabsWrapper = styled.div`
   width: 100%;
   display: flex;
   margin: 0 auto;
   box-sizing: border-box;
`;

export const TabsContentWrapper = styled(BaseTabsWrapper)`
   flex-direction: column;
   justify-content: center;
   align-items: center;
   max-width: ${cssConstants.contentWidth};
   z-index: ${cssConstants.zIndex.viewPageElement};
`;

const TabHeaderShadow = styled.div`
   z-index: -1;
   overflow: hidden;
   position: absolute;
   top: -1rem;
   right: -1rem;
   bottom: -1px;
   left: -1rem;

   &:after {
      content: '';
      display: block;
      position: absolute;
      top: 1rem;
      right: 1rem;
      bottom: 0;
      left: 1rem;
      z-index: -1;
      border-radius: 4px 4px 0 0;
      box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   }
`;

const TabBodyShadow = styled.div`
   z-index: 0;
   position: absolute;
   overflow: hidden;
   top: 0;
   right: -1rem;
   bottom: -1rem;
   left: -1rem;

   &:after {
      content: '';
      display: block;
      position: absolute;
      top: -1rem;
      right: 1rem;
      bottom: 1rem;
      left: 1rem;
      border-radius: 0 0 4px 4px;
      box-shadow: 0 3px 6px rgba(0,0,0,.15),0 0 2px rgba(0,0,0,.1);
   }
`;

const TabsBarWrapper = styled(BaseTabsWrapper)<{theme: TscThemeNames}>`
   position: relative;
   max-width: ${cssConstants.contentWidth};
   border-radius: 0.25rem 0.25rem 0 0;
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   border-bottom: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};
`;

const TabsPadding = styled.div`
   flex-grow: 1;
`;

const Card = styled.div<{theme: TscThemeNames}>`
   position: relative;
   max-width: ${cssConstants.contentWidth};
   width: 100%;
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   border-radius: 0 0 0.25rem 0.25rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const getHref = (tabName: string): string => UpsertQueryParamInUrl(window.location.href, Constants.queryParams.tab, tabName);

export const MediaTabsBase: React.FC<IMediaTabsProps & IStateMappedProps> = ({tab, setTab, theme, IsVideo, IsImage, Hash, MediaId, IsReady, IsYouTube}) => {
   const [loadingQuizData, setLoadingQuizData] = SetupWorkerState();
   const [loadingAnalyticsData, setLoadingAnalyticsData] = SetupWorkerState();
   const [loadingConversationData, setLoadingConversationData] = SetupWorkerState();
   const [loadingCaptionData, setLoadingCaptionData] = SetupWorkerState();

   const getUserAccessibleTabs = () => {
      if (config.watchConfig.isOwnerOrAdmin && IsVideo) {
         return adminAccessibleTabs;
      } else if (config.watchConfig.isOwnerOrAdmin && IsImage) {
         return adminAccessibleImageTabs;
      } else if (config.watchConfig.isCaptionAdminOrAssignee) {
         return captionAccessibileTabs;
      }
      return viewerAccessibleTabs;
   };

   const userAccessibleTabs = getUserAccessibleTabs();
   const useDefaultTab = !tab || !Object.values(tabNames).filter(tn => userAccessibleTabs.includes(tn)).includes(tab);
   useDefaultTab && tab && setTab(tabNames.details);

   const scrollTabContentIntoViewIfNeeded = () => {
      const tabsContentWrapper = document.getElementById('tabs-content-wrapper');
      const rect = tabsContentWrapper?.getBoundingClientRect();

      const inView = rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

      !inView && tabsContentWrapper?.scrollIntoView({block: 'end', behavior: 'smooth'});
   };

   React.useEffect(() => {
      const getQuizData = async () => {
         try {
            setLoadingQuizData({isWorking: true, hasError: false});
            updateQuizData(await quizApi.getQuizData(Hash));
            setLoadingQuizData({isWorking: false, hasError: false});
         } catch {
            setLoadingQuizData({isWorking: false, hasError: true});
         }
      };

      const getCaptioningStatus = async () => {
         setLoadingCaptionData({isWorking: true, hasError: false});
         try {
            updateCaptionDataState(await captionApi.getStatus(MediaId));
            setLoadingCaptionData({isWorking: false, hasError: false});
         } catch (e) {
            console.error(e);
            setLoadingCaptionData({isWorking: false, hasError: true});
         }
      };

      const getConversationData = async () => {
         try {
            setLoadingConversationData({isWorking: true, hasError: false});
            await updateConversationsFromServer(Hash);
            setLoadingConversationData({isWorking: false, hasError: false});
         } catch {
            setLoadingConversationData({isWorking: false, hasError: true});
         }
      };

      const getAnalyticsData = async () => {
         const maxTryLimit = 5;
         const retryDelay = 1500;

         const fetchAnalyticsData = (currentTries: number) => async () => {
            if (currentTries >= maxTryLimit) {
               setLoadingAnalyticsData({isWorking: false, hasError: true});
               return;
            }

            try {
               setLoadingAnalyticsData({isWorking: true, hasError: false});
               await updateAnalyticsDataFromServer(MediaId);
               setLoadingAnalyticsData({isWorking: false, hasError: false});
            } catch {
               // eslint-disable-next-line @typescript-eslint/no-misused-promises, no-magic-numbers
               window.setTimeout(fetchAnalyticsData(currentTries + 1), retryDelay);
            }
         };
         await fetchAnalyticsData(0)();
      };

      if (IsReady) {
         void getConversationData();
         if (config.watchConfig.isOwnerOrAdmin && IsVideo && !IsYouTube) {
            void getCaptioningStatus();
         }
         if (!config.featureSwitches.DisableAnalyticsAndQuizEditing) {
            void getQuizData();
            void getAnalyticsData();
         }
      }
   }, [IsReady]);

   let tabs: ITab[] = [];
   tabs.push(
      {
         text: tabNames.details,
         isSelected: tab === tabNames.details || useDefaultTab,
         onClick: () => setTab(tabNames.details),
         href: getHref(tabNames.details)
      }, {
         text: tabNames.privacy,
         isSelected: tab === tabNames.privacy,
         onClick: () => setTab(tabNames.privacy),
         href: getHref(tabNames.privacy)
      });

   if (IsReady) {
      if (!config.featureSwitches.DisableAnalyticsAndQuizEditing) {
         tabs.push(
            {
               text: tabNames.analytics,
               isSelected: tab === tabNames.analytics,
               onClick: () => setTab(tabNames.analytics),
               href: getHref(tabNames.analytics)
            }
         );
      }

      if (!IsYouTube) {
         if (IsVideo) {
            tabs.push(
               {
                  text: tabNames.accessibility,
                  isSelected: tab === tabNames.accessibility,
                  onClick: () => setTab(tabNames.accessibility),
                  href: getHref(tabNames.accessibility)
               });
            if (!config.featureSwitches.DisableMediaEditing) {
               tabs.push({
                  text: tabNames.editMedia,
                  isSelected: tab === tabNames.editMedia,
                  onClick: () => setTab(tabNames.editMedia),
                  href: getHref(tabNames.editMedia)
               });
            }
         }
         const conversationTabName = config.featureSwitches.UseSingleConversation ? tabNames.conversation : tabNames.conversations;
         tabs.push({
            text: conversationTabName,
            isSelected: tab === conversationTabName,
            onClick: () => setTab(conversationTabName),
            href: getHref(conversationTabName)
         });
      }

      if (!config.featureSwitches.DisableAnalyticsAndQuizEditing) {
         tabs.push(
            {
               text: tabNames.quizzing,
               isSelected: tab === tabNames.quizzing,
               onClick: () => setTab(tabNames.quizzing),
               href: getHref(tabNames.quizzing)
            });
      }
   }
   tabs = tabs.filter(t => userAccessibleTabs.includes(t.text));

   if (config.environmentData.IsLti) {
      tabs = tabs.filter((t: ITab) => ltiTabNames.indexOf(t.text) !== -1);
   }

   if (!tabs.find(t => t.text === tab)) {
      setTab(tabNames.details);
   }

   return (
      <>
         <TabsBarWrapper theme={theme} onClick={scrollTabContentIntoViewIfNeeded}>
            <Tabs tabs={tabs} />
            <TabHeaderShadow />
         </TabsBarWrapper>
         <TabsPadding id="tabs-content-wrapper">
            <TabsContentWrapper>
               <Card theme={theme}>
                  <TabBodyShadow/>
                  {(tab === tabNames.details || useDefaultTab) && <DetailsTab />}
                  {tab === tabNames.privacy && <PrivacyTab />}
                  {tab === tabNames.quizzing && <QuizzingTab initialIsWorking={loadingQuizData.isWorking} initialHasError={loadingQuizData.hasError} />}
                  {tab === tabNames.accessibility && <AccessibilityTab captionsWorkingState={loadingCaptionData}/>}
                  {tab === tabNames.editMedia && <EditMediaTab />}
                  {tab === tabNames.analytics && <AnalyticsTab isWorking={loadingAnalyticsData.isWorking} hasError={loadingAnalyticsData.hasError} />}
                  {tab === tabNames.conversations && <ConversationsTab isWorking={loadingConversationData.isWorking} hasError={loadingConversationData.hasError} />}
                  {tab === tabNames.conversation && <ConversationTab isWorking={loadingConversationData.isWorking} hasError={loadingConversationData.hasError} />}
               </Card>
            </TabsContentWrapper>
         </TabsPadding>
      </>
   );
};

export interface IMediaTabsProps {
   tab: string;
   setTab(tab: string): void;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   IsVideo: boolean;
   IsImage: boolean;
   Hash: string;
   MediaId: string;
   IsReady: boolean;
   IsYouTube: boolean;
}

const mapStatesToProps = (
   {tab, setTab}: IMediaTabsProps,
   {theme}: Partial<IStateMappedProps>,
   {IsVideo, IsImage, Hash, MediaId, IsReady, IsYouTube}: Partial<IStateMappedProps>
): IMediaTabsProps & IStateMappedProps => ({tab, setTab, theme, IsVideo, IsImage, Hash, MediaId, IsReady, IsYouTube});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(MediaTabsBase);
